import {
  Box,
  Button,
  Center,
  Flex,
  Image,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import useAppDispatch from 'hooks/shared/store/useAppDispatch';
import useAppSelector from 'hooks/shared/store/useAppSelector';
import MessageListLoaded from 'presentation/components/modules/chat/detail/MessageListLoaded';
import ErrorContainer from 'presentation/components/shared/ErrorContainer';
import {
  ButtonSize,
  ButtonVariant,
} from 'presentation/theme/components/button';
import { FC, useEffect, useRef, useState } from 'react';
import { useScroll, useUpdateEffect } from 'react-use';
import {
  useFetchMessageListQuery,
  useLazyCheckChatUpdatesQuery,
  useReadMessagesMutation,
} from 'rtkQuery/sharespro/queries/chat';
import { selectedWidgetChatSelector } from 'selectors/modules/chat/widgetChat';
import { useFilePicker } from 'use-file-picker';
import ChatDetailControl from './ChatDetailControl';
import UploadChatFilesModal from './UploadChatFilesModal';
import PreviewChatImagesModal from './PreviewChatImagesModal';
import arrowBack from 'assets/img/icons/arrow-back.svg';
import { useTranslation } from 'react-i18next';
import { widgetChatSlice } from 'slices/modules/chat/widgetChatSlice';
import { FontSize } from 'presentation/theme/fontSizes';
import { ChatMessageAuthorType } from 'enums/modules/chat/chatMessageAuthorType';
import { ChatMessageStatus } from 'enums/modules/chat/chatMessageStatus';
import useChatName from 'hooks/modules/chat/useChatName';

const EXTRA_SCROLL_POSITION_CHECKING = 10;

const ChatDetailWidgetContainer: FC = () => {
  const [page, setPage] = useState<number>(1);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState<boolean>(true);

  const chatRoomData = useAppSelector(selectedWidgetChatSelector);

  const { t } = useTranslation();

  const [checkMessagesUpdates, { isFetching: isCheckingMessages }] =
    useLazyCheckChatUpdatesQuery();

  const [readMessages] = useReadMessagesMutation();

  const dispatch = useAppDispatch();

  const scrollRef = useRef<HTMLDivElement>(null);
  const { y } = useScroll(scrollRef);

  const {
    isFetching: isMessagesFetching,
    currentData: messagesData,
    error: messagesError,
    refetch: refetchMessages,
  } = useFetchMessageListQuery(
    chatRoomData
      ? {
          chat_id: chatRoomData?.id,
          page,
        }
      : skipToken
  );

  const lastMessageId =
    messagesData && messagesData.items.length > 0
      ? messagesData.items[0].id
      : null;

  useEffect(() => {
    const scrollableElement = scrollRef.current;
    if (scrollableElement) {
      setIsScrolledToBottom(
        y >
          scrollableElement.scrollHeight -
            scrollableElement.clientHeight -
            EXTRA_SCROLL_POSITION_CHECKING
      );
    }
  }, [scrollRef, y]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!isCheckingMessages && chatRoomData && lastMessageId) {
        checkMessagesUpdates({
          message_id: lastMessageId,
          chat_id: chatRoomData.id,
        });
      }
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [isCheckingMessages, chatRoomData, lastMessageId]);

  useEffect(() => {
    if (lastMessageId && isScrolledToBottom) {
      scrollEndRef.current?.scrollIntoView();
    }
  }, [lastMessageId]);

  useEffect(() => {
    const lastMessage =
      messagesData && messagesData.items.length > 0
        ? messagesData.items[0]
        : null;

    if (
      isScrolledToBottom &&
      chatRoomData &&
      lastMessage &&
      lastMessage.authorType !== ChatMessageAuthorType.Investor &&
      lastMessage.status === ChatMessageStatus.Sent
    ) {
      readMessages(chatRoomData.id);
    }
  }, [isScrolledToBottom, lastMessageId]);

  const [openFilePicker, { plainFiles }] = useFilePicker({
    multiple: true,
  });
  const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
  const [imagesToPreview, setImagesToPreview] = useState<File[]>([]);

  const scrollEndRef = useRef<HTMLDivElement | null>(null);

  const {
    isOpen: isFileModalOpened,
    onOpen: openFilesModal,
    onClose: closeFilesModal,
  } = useDisclosure();

  const uploadFiles = (files: File[]): void => {
    if (files.length === 0 || isFileModalOpened) return;

    setFilesToUpload(files);
    openFilesModal();
  };

  useUpdateEffect(() => {
    uploadFiles(plainFiles);
  }, [plainFiles]);

  let wrapContentWithCenter = true;
  let allDataHasLoaded = false;
  let content;
  if (chatRoomData && messagesData) {
    wrapContentWithCenter = false;
    allDataHasLoaded = true;
    content = (
      <MessageListLoaded
        items={messagesData.items}
        pageCount={messagesData.pageCount}
        currentPage={messagesData.currentPage}
        setPage={setPage}
        isFetching={isMessagesFetching}
        scrollEndRef={scrollEndRef}
        roomType={chatRoomData.type}
      />
    );
  } else if (isMessagesFetching) {
    content = <Spinner />;
  } else if (messagesError) {
    content = (
      <ErrorContainer
        error={messagesError}
        onRetry={() => {
          if (messagesError) {
            refetchMessages();
          }
        }}
      />
    );
  } else {
    content = <Spinner />;
  }

  if (wrapContentWithCenter) {
    content = (
      <Center p="20px" w="100%" height="100%">
        {content}
      </Center>
    );
  }
  const name = chatRoomData !== null ? chatRoomData.name ?? useChatName(chatRoomData.type) : t('allChats');

  return (
    <>
      <Flex flexDirection="column" w="100%" height="100%">
        <Flex py="15px" px="20px" alignItems="center">
          <Button
            p="5px"
            size={ButtonSize.sm}
            variant={ButtonVariant.Ghost}
            mr="20px"
            onClick={() =>
              dispatch(widgetChatSlice.actions.setChat(null))
            }
          >
            <Image src={arrowBack} h="18px" />
          </Button>
          <Text fontSize={FontSize.lg} fontWeight={700} flex="1" noOfLines={1}>
            {name}
          </Text>
        </Flex>
        <Box overflowY="auto" flex="1" ref={scrollRef}>
          {content}
          <div ref={scrollEndRef} />
        </Box>
        {chatRoomData && (
          <ChatDetailControl
            chatId={chatRoomData.id}
            scrollEndRef={scrollEndRef}
            isDisabled={!allDataHasLoaded || isFileModalOpened || imagesToPreview.length > 0}
            openFilePicker={openFilePicker}
            onImagesPaste={setImagesToPreview}
          />
        )}
      </Flex>
      {chatRoomData && imagesToPreview.length > 0 && (
        <PreviewChatImagesModal
          initialFiles={imagesToPreview}
          onClose={() => setImagesToPreview([])}
          onSend={(files) => {
            setImagesToPreview([]);
            uploadFiles(files);
          }}
        />
      )}
      {chatRoomData && (
        <UploadChatFilesModal
          isOpen={isFileModalOpened}
          onClose={closeFilesModal}
          filesToUpload={filesToUpload}
          chatId={chatRoomData.id}
          scrollEndRef={scrollEndRef}
        />
      )}
    </>
  );
};

export default ChatDetailWidgetContainer;
